import React from 'react';
import PropTypes from 'prop-types';

/**
 * Loading variant options
 */
export const LOADING_VARIANTS = {
  SPINNER: 'spinner',
  DOTS: 'dots',
  PULSE: 'pulse',
};

/**
 * Loading size options
 */
export const LOADING_SIZES = {
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  XLARGE: 'xl',
};

/**
 * Enhanced Loading component for showing attractive loading states
 * 
 * @param {Object} props - Component props
 * @param {string} [props.text] - Loading text
 * @param {string} [props.subText] - Secondary loading text (smaller)
 * @param {string} [props.variant] - Loading style variant
 * @param {string} [props.size] - Loading indicator size
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.fullScreen] - Whether to display as a full-screen overlay
 * @param {string} [props.color] - Primary color for loading indicators
 * @returns {JSX.Element} Rendered component
 */
const Loading = ({
  text = 'Loading...',
  subText,
  variant = LOADING_VARIANTS.SPINNER,
  size = LOADING_SIZES.MEDIUM,
  className = '',
  fullScreen = false,
  color = 'blue',
}) => {
  // Color classes
  const colorClasses = {
    blue: 'border-blue-500 text-blue-500',
    green: 'border-green-500 text-green-500',
    purple: 'border-purple-500 text-purple-500',
    teal: 'border-teal-500 text-teal-500',
    orange: 'border-orange-500 text-orange-500',
  };
  
  const selectedColor = colorClasses[color] || colorClasses.blue;
  const borderColor = selectedColor.split(' ')[0];
  const textColor = selectedColor.split(' ')[1];
  
  // Size classes for spinner
  const spinnerSizeClasses = {
    [LOADING_SIZES.SMALL]: 'w-5 h-5 border-2',
    [LOADING_SIZES.MEDIUM]: 'w-8 h-8 border-3',
    [LOADING_SIZES.LARGE]: 'w-12 h-12 border-3',
    [LOADING_SIZES.XLARGE]: 'w-16 h-16 border-4',
  };
  
  // Size classes for dots
  const dotsSizeClasses = {
    [LOADING_SIZES.SMALL]: 'h-1.5 w-1.5',
    [LOADING_SIZES.MEDIUM]: 'h-2.5 w-2.5',
    [LOADING_SIZES.LARGE]: 'h-3.5 w-3.5',
    [LOADING_SIZES.XLARGE]: 'h-4 w-4',
  };
  
  // Size classes for text
  const textSizeClasses = {
    [LOADING_SIZES.SMALL]: 'text-xs',
    [LOADING_SIZES.MEDIUM]: 'text-sm',
    [LOADING_SIZES.LARGE]: 'text-base',
    [LOADING_SIZES.XLARGE]: 'text-lg',
  };

  // Render spinner variant
  const renderSpinner = () => (
    <div className="flex flex-col items-center justify-center">
      <div className={`animate-spin rounded-full border-t-transparent ${spinnerSizeClasses[size]} ${borderColor}`}></div>
      {text && (
        <p className={`mt-3 text-gray-300 ${textSizeClasses[size]} font-medium`}>{text}</p>
      )}
      {subText && (
        <p className="mt-1 text-gray-500 text-xs">{subText}</p>
      )}
    </div>
  );

  // Render dots variant
  const renderDots = () => (
    <div className="flex flex-col items-center justify-center">
      <div className="flex space-x-2">
        <div className={`${dotsSizeClasses[size]} ${textColor} opacity-90 rounded-full animate-bounce`} style={{ animationDelay: '0ms', animationDuration: '0.8s' }}></div>
        <div className={`${dotsSizeClasses[size]} ${textColor} opacity-90 rounded-full animate-bounce`} style={{ animationDelay: '200ms', animationDuration: '0.8s' }}></div>
        <div className={`${dotsSizeClasses[size]} ${textColor} opacity-90 rounded-full animate-bounce`} style={{ animationDelay: '400ms', animationDuration: '0.8s' }}></div>
      </div>
      {text && (
        <p className={`mt-3 text-gray-300 ${textSizeClasses[size]} font-medium`}>{text}</p>
      )}
      {subText && (
        <p className="mt-1 text-gray-500 text-xs">{subText}</p>
      )}
    </div>
  );
  
  // Render pulse variant
  const renderPulse = () => (
    <div className="flex flex-col items-center justify-center">
      <div className={`relative ${spinnerSizeClasses[size]}`}>
        <div className={`absolute inset-0 rounded-full ${textColor} opacity-75 animate-ping`}></div>
        <div className={`relative rounded-full ${textColor} opacity-90 ${spinnerSizeClasses[size]}`}></div>
      </div>
      {text && (
        <p className={`mt-3 text-gray-300 ${textSizeClasses[size]} font-medium`}>{text}</p>
      )}
      {subText && (
        <p className="mt-1 text-gray-500 text-xs">{subText}</p>
      )}
    </div>
  );

  // Content based on variant
  let content;
  if (variant === LOADING_VARIANTS.SPINNER) {
    content = renderSpinner();
  } else if (variant === LOADING_VARIANTS.DOTS) {
    content = renderDots();
  } else if (variant === LOADING_VARIANTS.PULSE) {
    content = renderPulse();
  } else {
    content = renderSpinner();
  }

  // Full screen overlay
  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900/90 backdrop-blur-sm z-50 animate-fadeIn">
        <div className={`bg-gray-800 p-8 rounded-xl shadow-2xl border border-gray-700 transform animate-scaleIn ${className}`}>
          {content}
        </div>
      </div>
    );
  }

  // Regular display
  return (
    <div className={`flex items-center justify-center p-5 ${className}`}>
      {content}
    </div>
  );
};

Loading.propTypes = {
  text: PropTypes.string,
  subText: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(LOADING_VARIANTS)),
  size: PropTypes.oneOf(Object.values(LOADING_SIZES)),
  className: PropTypes.string,
  fullScreen: PropTypes.bool,
  color: PropTypes.oneOf(['blue', 'green', 'purple', 'teal', 'orange']),
};

export default Loading; 